/**
 * Date helpers for the procurement demo. All dates are local calendar days
 * anchored at midnight, so relative operational data ("3 days ago") never
 * drifts with the time of day and ISO dates never shift across time zones.
 */

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const MS_PER_DAY = 86_400_000;

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function startOfToday(): Date {
  return startOfDay(new Date());
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Local midnight `n` days before today. */
export function daysAgo(n: number): Date {
  return addDays(startOfToday(), -n);
}

/** Local midnight `n` days after today. */
export function daysFromNow(n: number): Date {
  return addDays(startOfToday(), n);
}

/** Parse 'YYYY-MM-DD' as a local calendar day (no time zone shift). */
export function parseIsoDate(iso: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) {
    throw new Error(`Invalid ISO date: ${iso}`);
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Format as 'YYYY-MM-DD' using local calendar fields. */
export function toIsoDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** 'Oct 9, 2026' */
export function fmtDate(d: Date): string {
  return `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** 'Oct 9' */
export function fmtShortDate(d: Date): string {
  return `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
}

/** Whole calendar days from today to `d` (negative when in the past). */
export function daysUntil(d: Date): number {
  return Math.round(
    (startOfDay(d).getTime() - startOfToday().getTime()) / MS_PER_DAY,
  );
}

/** 'today' / 'in 24 days' / '3 days ago' */
export function fmtRelative(d: Date): string {
  const n = daysUntil(d);
  if (n === 0) return 'today';
  const abs = Math.abs(n);
  const unit = abs === 1 ? 'day' : 'days';
  return n > 0 ? `in ${abs} ${unit}` : `${abs} ${unit} ago`;
}

/** 'Oct 2026' */
export function monthLabel(d: Date): string {
  return `${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * The last `n` month labels, oldest first, ending with the current month.
 * With today in Oct 2026, `monthKeysBack(24)` is ['Nov 2024', ..., 'Oct 2026'].
 */
export function monthKeysBack(n: number): string[] {
  const now = startOfToday();
  const labels: string[] = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    labels.push(monthLabel(new Date(now.getFullYear(), now.getMonth() - i, 1)));
  }
  return labels;
}
